import { Icon } from "../../components/Icon";
import { Btn, Card, Meter, Badge, AIChip, Skeleton } from "../../components/kit";
import { useGo } from "../../lib/useGo";
import { fmt } from "../../data/mock";
import { useMe } from "../../lib/api";
import { useLessons, useStats, useHistory, useRecommendations } from "../../lib/hooks";

export function DashboardPage() {
  const go = useGo();
  const me = useMe();
  const live = useLessons();
  const stats = useStats();
  const recentLive = useHistory();
  const recommendations = useRecommendations();

  // The hero, metrics and plan all need the core data — skeleton until it's in.
  if (!me || !stats || !live) {
    return <div className="dash"><DashboardSkeleton /></div>;
  }

  // Live counts/credits/streak when signed in; seed figures otherwise.
  const allLessons = live?.tracks.flatMap((t) => t.lessons) ?? [];
  const masteredCount = stats?.mastered ?? (live ? allLessons.filter((l) => l.state === "mastered").length : 4);
  const totalCount = stats?.totalLessons ?? (live ? allLessons.length : 9);
  const inProgressCount = stats?.inProgress ?? (live ? allLessons.filter((l) => l.state === "active").length : 2);
  const masteredPct = totalCount > 0 ? Math.round((masteredCount / totalCount) * 100) : 0;
  const creditsLeft = me?.credits ? me.credits.balance : 360;
  const streakDays = stats?.streak ?? 11;

  // Time saved this month: live from /stats when signed in, else the seed figure.
  const ts = stats?.timeSaved;
  const timeSavedHours = ts ? ts.hours : 6.5;
  const timeSavedAsks = ts ? ts.asks : 24;
  const spark = ts && ts.spark.length ? ts.spark : [3, 5, 2, 6, 4, 7, 5, 8];
  const sparkMax = Math.max(1, ...spark);

  const C = 440;
  // week dots: live activity when available, else a seeded week.
  const week: { label: string; on: boolean; today: boolean }[] =
    stats?.week ??
    [["M", 1], ["T", 1], ["W", 1], ["T", 1], ["F", 1], ["S", 0], ["S", 2]].map(([label, v]) => ({
      label: label as string,
      on: (v as number) >= 1,
      today: v === 2,
    }));
  // Greeting + date, live from the clock.
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 18 ? "Good afternoon" : "Good evening";
  const dateLabel = `${now.toLocaleDateString(undefined, { weekday: "long" })} · ${now.toLocaleDateString(undefined, { month: "long", day: "numeric" })}`;

  // Live plan rows (each lesson with its track + position), when signed in.
  const planRows = (live?.tracks ?? []).flatMap((track) =>
    track.lessons.map((lesson, indexInTrack) => ({ lesson, track, indexInTrack }))
  );
  const activeRows = planRows.filter((r) => r.lesson.state === "active");
  const readyRows = planRows.filter((r) => r.lesson.state === "ready");
  const heroRow = activeRows[0] ?? readyRows[0] ?? null;
  const allCaughtUp = !!live && heroRow === null && planRows.length > 0;

  // Hero "Continue learning" — live lesson when available, else the seed demo.
  const heroIsActive = heroRow?.lesson.state === "active";
  const heroTitle = heroRow ? heroRow.lesson.name : allCaughtUp ? "You're all caught up" : "Claude for design critique";
  const heroEye = heroRow ? (heroIsActive ? "Continue learning" : "Up next") : "Continue learning";
  const heroTrackName = heroRow ? heroRow.track.group : "Context engineering";
  const heroPosition = heroRow ? `Lesson ${heroRow.indexInTrack + 1} of ${heroRow.track.lessons.length}` : "Lesson 2 of 5";
  const heroRingPct = heroRow ? (heroIsActive ? heroRow.lesson.pct : masteredPct) : 60;
  const heroCta = heroIsActive ? "Resume lesson" : allCaughtUp ? "Review lessons" : "Start lesson";

  // "Continue your plan" — in-progress first, then ready; seed when signed out.
  const upnext = live
    ? [...activeRows, ...readyRows].slice(0, 3).map((r) => ({
        t: r.lesson.name,
        meta: `${r.track.group} · ${r.lesson.level}${r.lesson.dur ? ` · ${r.lesson.dur}` : ""}`,
        state: r.lesson.state,
        pct: r.lesson.pct,
        ic: r.track.icon,
      }))
    : [
        { t: "Context engineering", meta: "Skills · Prompted", state: "active", pct: 35, ic: "brain" },
        { t: "Cursor for design tokens", meta: "Tools · Guided", state: "ready", pct: 0, ic: "wrench" },
        { t: "Evaluating AI output", meta: "Skills · Guided", state: "ready", pct: 0, ic: "brain" },
      ];

  // "Recommended by Claude" — the engine's recommendations (priority order +
  // rationale) from /recommendations; ready lessons as interim while it loads;
  // seed when signed out.
  const recs =
    recommendations && recommendations.length > 0
      ? recommendations.slice(0, 2).map((r) => ({ t: r.title, d: r.reason }))
      : readyRows.filter((r) => r.lesson !== heroRow?.lesson).slice(0, 2).map((r) => ({ t: r.lesson.name, d: r.lesson.desc }));

  // Time-saved tile label: per-week from real lesson reports, else this-month estimate.
  const tsFromReports = ts?.fromReports === true;
  const timeSavedFoot = tsFromReports
    ? `per week · ${ts?.skills ?? 0} skill${(ts?.skills ?? 0) === 1 ? "" : "s"}`
    : `this month · ${timeSavedAsks} ask${timeSavedAsks === 1 ? "" : "s"}`;

  // Credits reset countdown from the real billing-cycle end, when available.
  const resetAt = me?.credits?.resetAt ?? null;
  const resetDays = resetAt ? Math.max(0, Math.ceil((resetAt - now.getTime()) / 86_400_000)) : null;
  const creditsFoot =
    resetDays != null
      ? `Resets in ${resetDays} day${resetDays === 1 ? "" : "s"}`
      : me?.credits?.included
        ? `of ${fmt(me.credits.included)} this cycle`
        : "Resets monthly";

  return (
    <div className="dash">
      <div className="dash-hello">
        <div>
          <div className="hello-eye">{dateLabel}</div>
          <h1>{greeting}{me?.name ? `, ${me.name.split(" ")[0]}` : ""}</h1>
        </div>
        <div className="hello-streak">
          <Icon name="flame" size={15} strokeWidth={2.2} />{streakDays}-day streak
        </div>
      </div>

      <div className="hero">
        <div className="hero-content">
          <div className="hero-eye">{heroEye}</div>
          <h2>{heroTitle}</h2>
          <p className="hero-meta">
            {allCaughtUp ? (
              <>Every available lesson complete — review or explore more.</>
            ) : (
              <>
                <Icon name="layers" size={14} />{heroTrackName}<span className="dotsep">·</span>{heroPosition}
                {heroRow?.lesson.dur ? (<><span className="dotsep">·</span>{heroRow.lesson.dur}</>) : null}
              </>
            )}
          </p>
          <div className="hero-cta">
            <Btn variant="live" icon={allCaughtUp ? "rotate-ccw" : "play"} onClick={() => go("lessons")}>{heroCta}</Btn>
            <button className="hero-ghost" onClick={() => go("curriculum")}>
              <Icon name="list-checks" size={15} />View plan
            </button>
          </div>
        </div>
        <div className="hero-ring">
          <svg viewBox="0 0 172 172">
            <circle className="hr-track" cx="86" cy="86" r="70" />
            <circle className="hr-prog" cx="86" cy="86" r="70" strokeDasharray={C} strokeDashoffset={C * (1 - heroRingPct / 100)} />
            <circle className="hr-faint" cx="86" cy="86" r="52" />
            <circle className="hr-faint" cx="86" cy="86" r="36" />
          </svg>
          <div className="hr-orbit">
            <span className="hr-dot" />
          </div>
          <div className="hr-center">
            <b>{heroRingPct}%</b>
            <span>complete</span>
          </div>
        </div>
      </div>

      <div className="metric-grid">
        <div className="metric accent">
          <div className="m-head">
            <span className="m-ic"><Icon name="flame" size={16} strokeWidth={2} /></span>
            <span className="m-label">Day streak</span>
          </div>
          <div className="m-val">{streakDays}<span>days</span></div>
          <div className="week">
            {week.map((w, i) => (
              <i key={i} className={w.today ? "today" : w.on ? "on" : ""}>{w.label}</i>
            ))}
          </div>
        </div>
        <div className="metric">
          <div className="m-head">
            <span className="m-ic"><Icon name="graduation-cap" size={16} strokeWidth={2} /></span>
            <span className="m-label">Lessons complete</span>
          </div>
          <div className="m-val">{masteredCount}<span>of {totalCount}</span></div>
          <div className="bar-viz"><i style={{ width: masteredPct + "%" }} /></div>
          <div className="m-foot up"><Icon name="arrow-up-right" size={12} strokeWidth={2.4} />{inProgressCount} in progress</div>
        </div>
        <div className="metric">
          <div className="m-head">
            <span className="m-ic"><Icon name="zap" size={16} strokeWidth={2} /></span>
            <span className="m-label">Credits left</span>
          </div>
          <div className="m-val">{fmt(creditsLeft)}</div>
          <div className="gauge-mini"><i style={{ width: Math.min(100, me?.credits?.included ? (creditsLeft / me.credits.included) * 100 : 7) + "%" }} /></div>
          <div className="m-foot"><Icon name="clock" size={12} />{creditsFoot}</div>
        </div>
        <div className="metric">
          <div className="m-head">
            <span className="m-ic"><Icon name="sparkles" size={16} strokeWidth={2} /></span>
            <span className="m-label">Time saved</span>
          </div>
          <div className="m-val">{timeSavedHours}<span>hrs</span></div>
          <div className="spark">
            {spark.map((v, i) => (
              <i key={i} className={v >= sparkMax && sparkMax > 0 ? "hi" : ""} style={{ height: Math.max(8, (v / sparkMax) * 100) + "%" }} />
            ))}
          </div>
          <div className="m-foot">{timeSavedFoot}</div>
        </div>
      </div>

      <div className="dash-cols">
        <div className="stack-lg">
          <Card
            title="Continue your plan"
            icon="route"
            iconColor="var(--live-dim)"
            count={`${upnext.length} up next`}
            action={<Btn variant="ghost" size="sm" iconRight="arrow-up-right" onClick={() => go("lessons")}>All lessons</Btn>}
            pad={false}
          >
            <div className="next-list">
              {upnext.length === 0 && (
                <div className="nx">
                  <span className="nx-ic mastered"><Icon name="check-check" size={18} /></span>
                  <div className="nx-body">
                    <div className="nx-t">You're all caught up</div>
                    <div className="nx-meta">Every available lesson is complete.</div>
                  </div>
                </div>
              )}
              {upnext.map((l, i) => (
                <div className="nx" key={i}>
                  <span className={`nx-ic ${l.state}`}><Icon name={l.ic} size={18} /></span>
                  <div className="nx-body">
                    <div className="nx-t">{l.t}</div>
                    <div className="nx-meta">
                      {l.meta}
                      {l.state === "active" && (<><span className="dotsep">·</span>{l.pct}%</>)}
                    </div>
                    {l.state === "active" && <div className="nx-prog"><Meter pct={l.pct} height={4} /></div>}
                  </div>
                  <Btn variant={l.state === "active" ? "live" : "ghost"} size="sm" icon="play">
                    {l.state === "active" ? "Resume" : "Start"}
                  </Btn>
                </div>
              ))}
            </div>
          </Card>

          <Card
            title="Recommended by Claude"
            icon="sparkles"
            iconColor="var(--violet-500)"
            action={<AIChip />}
            pad={false}
          >
            <div className="next-list">
              {recs.length === 0 && (
                <div className="rec">
                  <span className="rec-ic"><Icon name="sparkles" size={16} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="rec-t">Nothing queued right now</div>
                    <div className="rec-d">New lessons appear here as you progress.</div>
                  </div>
                </div>
              )}
              {recs.map((s, i) => (
                <div className="rec" key={i}>
                  <span className="rec-ic"><Icon name="sparkles" size={16} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="rec-t">{s.t}</div>
                    <div className="rec-d">{s.d}</div>
                  </div>
                  <Btn variant="ghost" size="sm" icon="plus" onClick={() => go("curriculum")}>Add</Btn>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card
          title="Recent activity"
          icon="history"
          action={<Btn variant="ghost" size="sm" iconRight="arrow-up-right" onClick={() => go("history")}>View all</Btn>}
          pad={false}
        >
          <div className="tl">
            {recentLive === null
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div className="tl-row" key={i}>
                    <div className="tl-rail"><span className="tl-line" /><span className="tl-dot helper" /></div>
                    <div className="tl-body" style={{ display: "flex", flexDirection: "column", gap: 7 }}><Skeleton w="70%" h={13} /><Skeleton w={80} h={11} /></div>
                  </div>
                ))
              : recentLive.slice(0, 5).map((h, i) => (
                  <div className="tl-row" key={i}>
                    <div className="tl-rail">
                      <span className="tl-line" />
                      <span className={`tl-dot ${h.mode === "Coach" ? "coach" : "helper"}`} />
                    </div>
                    <div className="tl-body">
                      <div className="tl-t">{h.t}</div>
                      <div className="tl-meta">
                        <Badge tone={h.mode === "Coach" ? "live" : "neutral"}>{h.mode}</Badge>
                        <span className="tl-ago">{h.ago}</span>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <>
      <div className="dash-hello">
        <div><Skeleton w={120} h={12} style={{ marginBottom: 8 }} /><Skeleton w={220} h={28} /></div>
        <Skeleton w={120} h={34} r={999} />
      </div>
      <Skeleton h={150} r={18} style={{ marginBottom: 18 }} />
      <div className="metric-grid">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={130} r={16} />)}
      </div>
      <div className="dash-cols">
        <div className="stack-lg">
          <Skeleton h={220} r={16} />
          <Skeleton h={160} r={16} />
        </div>
        <Skeleton h={300} r={16} />
      </div>
    </>
  );
}
